import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtDA } from "@/lib/format";
import { Plus, Search, Users, Loader2, ChevronRight, Pencil } from "lucide-react";
import { toast } from "sonner";

const emptyForm = () => ({ societe_id: "", raison_sociale: "", adresse: "", telephone: "", email: "", rc: "", nif: "", nis: "", article: "", commune: "", wilaya: "", activite: "", commercial: "", conditions_tarifaires: "", delai_paiement: 0, plafond_credit: 0, observations: "" });

export default function Clients() {
  const { societes, societeParam, societeId } = useApp();
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients", { params: { ...societeParam, search: search || undefined } });
      setItems(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [societeId, search]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    const f = emptyForm();
    if (societeId !== "all") f.societe_id = societeId; else if (societes.length >= 1) f.societe_id = societes[0].id;
    setForm(f); setEditingId(null); setOpen(true);
  };
  const openEdit = (c) => {
    setForm({
      societe_id: c.societe_id, raison_sociale: c.raison_sociale || "", adresse: c.adresse || "",
      telephone: c.telephone || "", email: c.email || "", rc: c.rc || "", nif: c.nif || "", nis: c.nis || "",
      article: c.article || "", commune: c.commune || "", wilaya: c.wilaya || "", activite: c.activite || "",
      commercial: c.commercial || "", conditions_tarifaires: c.conditions_tarifaires || "",
      delai_paiement: c.delai_paiement || 0, plafond_credit: c.plafond_credit || 0, observations: c.observations || "",
    });
    setEditingId(c.id); setOpen(true);
  };
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.societe_id) return toast.error("Sélectionnez une société.");
    if (!form.raison_sociale) return toast.error("La raison sociale est requise.");
    setSaving(true);
    try {
      const payload = { ...form, delai_paiement: Number(form.delai_paiement) || 0, plafond_credit: Number(form.plafond_credit) || 0 };
      if (editingId) {
        await api.put(`/clients/${editingId}`, payload);
        toast.success("Client modifié.");
      } else {
        await api.post("/clients", payload);
        toast.success("Client créé.");
      }
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Clients" subtitle="Fiches clients et créances" testid="clients-page"
        actions={can("clients") ? <Button onClick={openNew} className="gap-2" data-testid="new-client-btn"><Plus className="h-4 w-4" /> Nouveau client</Button> : null} />

      <div className="relative w-full sm:w-72 mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input data-testid="client-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher…" className="pl-9" />
      </div>

      <div className="rounded-xl border bg-card overflow-hidden">
        {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          : items.length === 0 ? <EmptyState icon={Users} title="Aucun client" hint="Ajoutez vos clients pour suivre commandes, factures et règlements." />
          : (
            <div className="overflow-x-auto">
              <Table data-testid="clients-table">
                <TableHeader><TableRow className="bg-muted/50"><TableHead>Raison sociale</TableHead><TableHead>Téléphone</TableHead><TableHead>Commercial</TableHead><TableHead className="text-right">Créance</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {items.map((c) => (
                    <TableRow key={c.id} data-testid={`client-row-${c.id}`} className="cursor-pointer" onClick={() => navigate(`/clients/${c.id}`)}>
                      <TableCell className="font-medium">{c.raison_sociale}</TableCell>
                      <TableCell className="text-sm">{c.telephone || "—"}</TableCell>
                      <TableCell className="text-sm">{c.commercial || "—"}</TableCell>
                      <TableCell className={`tabular text-right font-semibold ${c.solde > 0 ? "text-warning" : "text-muted-foreground"}`}>{fmtDA(c.solde)}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          {can("clients") && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(c)} data-testid={`edit-client-${c.id}`}><Pencil className="h-4 w-4" /></Button>}
                          <ChevronRight className="h-4 w-4 text-muted-foreground cursor-pointer" onClick={() => navigate(`/clients/${c.id}`)} />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-popover max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="client-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editingId ? "Modifier le client" : "Nouveau client"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Société *</Label>
              <Select value={form.societe_id} onValueChange={(v) => setField("societe_id", v)}>
                <SelectTrigger data-testid="client-societe"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent className="bg-popover">{societes.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Raison sociale *</Label><Input data-testid="client-raison" value={form.raison_sociale} onChange={(e) => setField("raison_sociale", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Téléphone</Label><Input value={form.telephone} onChange={(e) => setField("telephone", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>E-mail</Label><Input value={form.email} onChange={(e) => setField("email", e.target.value)} /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Adresse</Label><Input value={form.adresse} onChange={(e) => setField("adresse", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Commune</Label><Input data-testid="client-commune" value={form.commune} onChange={(e) => setField("commune", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Wilaya</Label><Input data-testid="client-wilaya" value={form.wilaya} onChange={(e) => setField("wilaya", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Activité</Label><Input data-testid="client-activite" value={form.activite} onChange={(e) => setField("activite", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Commercial responsable</Label><Input value={form.commercial} onChange={(e) => setField("commercial", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Conditions tarifaires</Label><Input value={form.conditions_tarifaires} onChange={(e) => setField("conditions_tarifaires", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Délai de paiement (jours)</Label><Input type="number" value={form.delai_paiement} onChange={(e) => setField("delai_paiement", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Plafond de crédit (DA)</Label><Input type="number" value={form.plafond_credit} onChange={(e) => setField("plafond_credit", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>N° inscription (RC)</Label><Input data-testid="client-rc" value={form.rc} onChange={(e) => setField("rc", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>NIF</Label><Input data-testid="client-nif" value={form.nif} onChange={(e) => setField("nif", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>NIS</Label><Input data-testid="client-nis" value={form.nis} onChange={(e) => setField("nis", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Article d'imposition</Label><Input data-testid="client-article" value={form.article} onChange={(e) => setField("article", e.target.value)} /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Observations</Label><Textarea rows={2} value={form.observations} onChange={(e) => setField("observations", e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={submit} disabled={saving} data-testid="client-submit" className="gap-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
